import { useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Trash2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Row = ({ c, categories, onChanged }) => {
  const [name, setName] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const rename = async () => {
    if (!name?.trim() || busy) return;
    setBusy(true);
    try { await api.patch(`/onboarding/categories/${c.key}`, { name }); toast.success("Category renamed"); setName(null); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (reassign) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/onboarding/categories/${c.key}${reassign ? `?reassign_to=${reassign}` : ""}`);
      toast.success(data.moved ? `Category deleted — ${data.moved} document(s) moved` : "Category deleted"); setDel(null); onChanged();
    } catch (e) {
      if (e?.response?.status === 409 && !reassign) { setDel({ msg: e.response.data.detail, to: "" }); } else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <li className="py-2" data-testid={`cat-row-${c.key}`}>
      <div className="flex items-center gap-2">
        {name === null ? <span className="text-sm text-slate-800 flex-1">{c.label}</span>
          : <><Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), rename())} className="h-8 text-sm flex-1" autoFocus data-testid={`cat-rename-input-${c.key}`} />
            <Button type="button" size="sm" className="h-8 bg-teal-600 hover:bg-teal-700" onClick={rename} disabled={busy || !name.trim()} data-testid={`cat-rename-save-${c.key}`}><Check className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setName(null)}><X className="h-3.5 w-3.5" /></Button></>}
        {name === null && <>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setName(c.label)} data-testid={`cat-rename-${c.key}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Rename</Button>
          <Button type="button" size="sm" variant="ghost" className="h-8 text-xs text-rose-600 hover:text-rose-700" onClick={() => remove()} disabled={busy} data-testid={`cat-delete-${c.key}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </>}
      </div>
      {del && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 space-y-2" data-testid={`cat-reassign-${c.key}`}>
          <div>{del.msg}</div>
          <div className="flex gap-2 items-center">
            <Select value={del.to} onValueChange={(v) => setDel((d) => ({ ...d, to: v }))}>
              <SelectTrigger className="h-8 text-xs bg-white w-[260px]" data-testid={`cat-reassign-select-${c.key}`}><SelectValue placeholder="Move documents to…" /></SelectTrigger>
              <SelectContent>{categories.filter((x) => x.key !== c.key).map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" size="sm" variant="destructive" className="h-8 text-xs" disabled={!del.to || busy} onClick={() => remove(del.to)} data-testid={`cat-reassign-confirm-${c.key}`}>Move & delete</Button>
            <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setDel(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </li>
  );
};

export const ManageCategoriesDialog = ({ open, onOpenChange, categories, onChanged }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" data-testid="manage-categories-dialog">
      <DialogHeader><DialogTitle>Manage categories</DialogTitle><DialogDescription>Rename or delete the categories you added. Built-in categories (PAN, GST, …) cannot be changed. Deleting a category in use asks you to move its documents first.</DialogDescription></DialogHeader>
      <ul className="divide-y divide-slate-100 max-h-[55vh] overflow-y-auto">
        {categories.filter((c) => c.custom).map((c) => <Row key={c.key} c={c} categories={categories} onChanged={onChanged} />)}
        {!categories.some((c) => c.custom) && <li className="py-3 text-sm text-slate-500">No custom categories yet.</li>}
      </ul>
    </DialogContent>
  </Dialog>
);
